import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { chineseLang } from '../../i18n/chinese'
import { formatNumber } from '../../i18n/format'
import { wordMeaning } from '../../i18n/localizedContent'
import { AppUseSelector } from '../../redux/hooks'
import store from '../../redux/store'
import { POCKET_LIMIT, PocketWord } from '../../redux/reducers/pocketReducer'
import { getWordDetail } from '../../redux/actions/storyAction'
import { renderConverterWord, wordSimplified } from 'utils/helperFunction'
import { useWordAudio } from '../../hooks/useWordAudio'
import { AudioPlayIcon } from '../icons/icons'
import Word from '../RenderStroyComponent/Word'
import WordDetail from '../word-detail'
import WordExamplesModal from '../modal/word-examples-modal'
import { CardHolderView } from './cardHolderView'
import { PocketIcon } from './PocketIcon'
import { syncPocketRules } from './pocketUnderline'
import {
  loadPocket,
  registerPocketSurface,
  swapInto,
  SwapRequest,
  takeOut,
  trackPocketOpened,
} from './pocketController'
import './wordPocket.css'

const PUNCTUATION = /^[\s。，、；：？！…—·“”‘’「」『』（）《》〈〉【】.,;:?!'"()\-]+$/

const Chevron = () => (
  <span className='pk-chev' aria-hidden='true'>
    <svg width='12' height='8' viewBox='0 0 12 8'>
      <path d='M1 1.5l5 5 5-5' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  </span>
)

const CloseIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' aria-hidden='true'>
    <path d='M1 1l12 12M13 1L1 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
  </svg>
)

interface WordPocketProps {
  /** Show the holder on this page (reading steps and the learner home). Pocket marks apply everywhere. */
  show: boolean
  /** A reading step: on desktop the holder keeps one height across steps, footer or not. */
  readingStep?: boolean
}

// Just above the desktop reading footer (104px tall), on every step, so the holder never jumps.
const DESKTOP_STEP_BOTTOM = 118
// The narrowest gap between the phone footer's buttons the holder docks into.
const DOCK_MIN_GAP = 84

// The wordmark debossed on the sleeve.
const BRAND_LOGO = `url('${process.env.PUBLIC_URL || ''}/images/maayot-green-logo-small.svg')`
const BRAND_MASK: React.CSSProperties = { WebkitMaskImage: BRAND_LOGO, maskImage: BRAND_LOGO }

/**
 * The word pocket: a card holder in the bottom-left corner holding up to five words the learner
 * wants to focus on. Opening it lists the words; each one opens onto the sentence it came from.
 */
// The row's ARIA role, set only outside the swap view.
const ROW_ROLE = 'button'

const WordPocket: React.FC<WordPocketProps> = ({ show, readingStep = false }) => {
  const { words, pending } = AppUseSelector(({ pocket }) => pocket)
  const { credentials } = AppUseSelector(({ user }) => user)
  const { storyDictionary } = AppUseSelector(({ story }) => story)
  const characterPreference = credentials?.customFields?.character
  const memberId = credentials?.memberId
  const { t } = useTranslation('reader')
  const zhLang = chineseLang(characterPreference)
  const pocketLabel = t('pocket.dockLabel', { count: words.length, limit: POCKET_LIMIT })

  const rootRef = useRef<HTMLDivElement>(null)
  const fxRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<CardHolderView | null>(null)
  const dockRef = useRef<HTMLButtonElement>(null)
  const dockIconRef = useRef<HTMLSpanElement>(null)
  const [docked, setDocked] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const incomingRef = useRef<HTMLSpanElement>(null)
  const examplesRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [swap, setSwap] = useState<SwapRequest | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [leavingRow, setLeavingRow] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const toastTimer = useRef<any>(null)

  // Its own word panel, for words tapped inside the pocket.
  const [detailVisible, setDetailVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)

  const { playWordAudio } = useWordAudio()

  useEffect(() => {
    loadPocket()
    syncPocketRules()
    return store.subscribe(syncPocketRules)
  }, [])

  // Sit just above the reading steps' sticky footer, whose height differs by page and screen
  // (on phones the audio player gets its own row) and which renders only once the story loads.
  useEffect(() => {
    if (!show) return
    let frame = 0
    const measure = () => {
      frame = 0
      const footer = document.getElementById('navigation-footer')
      let top = window.innerHeight
      if (footer) {
        const parts = [footer, ...Array.from(footer.children), ...Array.from(footer.querySelectorAll('.section-audio-mobile'))]
        parts.forEach((el) => {
          const r = el.getBoundingClientRect()
          if (r.height && r.top < top) top = r.top
        })
      }
      const desktop = !window.matchMedia?.('(max-width: 767px)').matches
      const floor = desktop && readingStep ? DESKTOP_STEP_BOTTOM : 20
      const bottom = footer ? Math.max(floor, Math.round(window.innerHeight - top + 14)) : floor
      const rootStyle = document.documentElement.style
      rootStyle.setProperty('--pk-bottom', `${bottom}px`)
      // On phones the pocket becomes a button between the footer's Back and Next, off the story.
      const back = footer?.querySelector('.bottom-navigation-left .button-2')?.getBoundingClientRect()
      const next = footer?.querySelector('.bottom-navigation-right .button-2')?.getBoundingClientRect()
      const dock = !desktop && !!back?.width && !!next?.width && next.left - back.right >= DOCK_MIN_GAP
      if (dock && back && next) {
        rootStyle.setProperty('--pk-dock-x', `${Math.round((back.right + next.left) / 2)}px`)
        rootStyle.setProperty('--pk-dock-bottom', `${Math.round(window.innerHeight - back.bottom)}px`)
      }
      setDocked(dock)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', schedule)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', schedule)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [show, readingStep])

  const showToast = useCallback((message: string) => {
    setToast(message)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 4000)
  }, [])

  useEffect(() => {
    if (!show || !rootRef.current || !fxRef.current) return
    const view = new CardHolderView(rootRef.current, fxRef.current)
    viewRef.current = view
    view.sync(store.getState().pocket.words.map((w) => w.word))
    const onResize = () => view.relayout()
    window.addEventListener('resize', onResize)
    registerPocketSurface({
      fly: (word, origin, wordsAfter) => view.fly(word, origin, wordsAfter),
      markFresh: (word) => view.markFresh(word),
      release: (word, mode) => view.release(word, mode),
      openSwap: (request) => {
        setSwap(request)
        setOpen(true)
      },
      toast: showToast,
    })
    return () => {
      window.removeEventListener('resize', onResize)
      registerPocketSurface(null)
      viewRef.current = null
    }
  }, [show, showToast])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.labelFor = (w) => renderConverterWord(w, characterPreference)
    view.sync(words.map((w) => w.word))
  }, [words, characterPreference, show])

  // A card opens the pocket with its word already open; the other words stay listed around it.
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.onCardClick = (word) => {
      if (swap) return
      if (!open) trackPocketOpened()
      setOpenRow(word)
      setOpen(true)
    }
  }, [swap, open, show])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.dockTarget = dockIconRef.current
    view.setDocked(docked)
  }, [docked, show])

  useEffect(() => {
    viewRef.current?.setActive(open && !swap ? openRow : null)
  }, [open, openRow, swap, show])

  useEffect(() => {
    if (!open || !openRow) return
    const t = setTimeout(() => {
      sheetRef.current
        ?.querySelector(`[data-row="${CSS.escape(openRow)}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 280)
    return () => clearTimeout(t)
  }, [open, openRow])

  useEffect(() => {
    if (!words.length) {
      setOpen(false)
      setSwap(null)
    }
  }, [words.length])

  const closeSheet = useCallback(() => {
    setOpen(false)
    setSwap(null)
  }, [])

  // Close on Escape, and on a click anywhere outside the pocket and its word panel.
  useEffect(() => {
    if (!open && !detailVisible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (detailVisible) setDetailVisible(false)
      else closeSheet()
    }
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const inPanel = panelRef.current?.contains(target) || examplesRef.current?.contains(target)
      const inSheet =
        sheetRef.current?.contains(target) || rootRef.current?.contains(target) || dockRef.current?.contains(target)
      if (detailVisible && !inPanel && !target.closest('[data-specific-buttons]')) {
        setDetailVisible(false)
        setActiveIndex('-1')
      }
      if (open && !inSheet && !inPanel && !target.closest('.word_detail_modal_main_div')) closeSheet()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, detailVisible, closeSheet])

  const toggleSheet = () => {
    if (open) return closeSheet()
    // The sleeve opens the plain list; a card opens its own word.
    setOpenRow(null)
    setOpen(true)
    trackPocketOpened()
  }

  const openWord = (event: React.MouseEvent, entry: PocketWord, token: string, key: string) => {
    event.stopPropagation()
    const word = (event.currentTarget as HTMLElement).innerText?.trim() || token
    if (!memberId || PUNCTUATION.test(word)) return
    setActiveIndex(key)
    setDetailVisible(true)
    getWordDetail({
      detail: {
        word,
        pinyin: '',
        english: '',
        sentenceString: entry.sentenceWords.join(''),
        sentenceWords: entry.sentenceWords,
      },
      payload: { word: wordSimplified(word), memberId },
      wordType: storyDictionary?.[wordSimplified(word)]?.mastery ?? '',
    })
  }

  const release = (word: string, mode: 'remove' | 'mastered') => {
    // Fold the row away from its current height, so the list closes up gently instead of jumping.
    const row = sheetRef.current?.querySelector<HTMLElement>(`[data-row="${CSS.escape(word)}"]`)
    if (row) {
      row.style.height = `${row.offsetHeight}px`
      void row.offsetHeight
      requestAnimationFrame(() => {
        row.style.height = '0px'
      })
    }
    setLeavingRow(word)
    takeOut(word, mode).finally(() => setLeavingRow(null))
  }

  const swapOut = (word: string) => {
    if (!swap) return
    const request = swap
    // Freeze the waiting word where it sits, so it can still fly after the sheet closes.
    const origin = incomingRef.current
    let ghost: HTMLElement | null = null
    if (origin && fxRef.current) {
      const r = origin.getBoundingClientRect()
      ghost = origin.cloneNode(true) as HTMLElement
      Object.assign(ghost.style, { position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, margin: '0' })
      fxRef.current.appendChild(ghost)
    }
    closeSheet()
    swapInto(word, request, ghost).finally(() => ghost?.remove())
  }

  const rows = useMemo(() => [...words].reverse(), [words])
  const label = (w: string) => renderConverterWord(w, characterPreference)

  const renderSentence = (entry: PocketWord) =>
    entry.sentenceWords.map((token, i) => {
      const key = `POCKET-${entry.word}-${i}`
      if (PUNCTUATION.test(token)) return <span key={key}>{token}</span>
      return (
        <Word
          key={key}
          index={key}
          text={token}
          chineseWordFontSize='17px'
          isActive={detailVisible && activeIndex === key}
          onClick={(e) => openWord(e, entry, token, key)}
        />
      )
    })

  return (
    <>
      {show && (
        <div className='word-pocket pk-gone' ref={rootRef} aria-hidden={!words.length}>
          <div className='pk-inner'>
            <div className='pk-cards' />
            <svg className='pk-sleeve' aria-hidden='true' />
            <span className='pk-brand' aria-hidden='true'>
              <span style={BRAND_MASK} />
            </span>
            <button
              type='button'
              className='pk-hit'
              aria-haspopup='dialog'
              aria-expanded={open}
              aria-label={pocketLabel}
              onClick={toggleSheet}
            />
          </div>
        </div>
      )}

      {show && docked && (
        // Styled like the footer's Back button. Kept mounted while empty so a first word has somewhere to fly.
        <button
          ref={dockRef}
          type='button'
          className={`button-2 bg-gray-4 w-button pk-dock${words.length || pending.length ? '' : ' pk-dock-empty'}`}
          aria-haspopup='dialog'
          aria-expanded={open}
          aria-label={pocketLabel}
          onClick={toggleSheet}
        >
          <span className='pk-dock-icon' ref={dockIconRef}>
            <PocketIcon filled size={20} />
          </span>
          {words.length > 0 && <span className='pk-dock-count'>{words.length}</span>}
        </button>
      )}

      {show && (
        <div
          ref={sheetRef}
          className={`pk-sheet${open ? ' open' : ''}`}
          role='dialog'
          aria-label={t('pocket.title')}
          aria-hidden={!open}
        >
          <div className='pk-head'>
            <p className='box-title'>{swap ? t('pocket.fullTitle') : t('pocket.title')}</p>
            {!swap && (
              <span className='pk-count'>
                {t('pocket.count', { used: formatNumber(words.length), limit: formatNumber(POCKET_LIMIT) })}
              </span>
            )}
            <button type='button' className='pk-close' aria-label={t('pocket.close')} onClick={closeSheet}>
              <CloseIcon />
            </button>
          </div>
          {swap && (
            <p className='pk-swap'>
              <Trans
                t={t}
                i18nKey='pocket.swapPrompt'
                values={{ word: label(swap.entry.word) }}
                components={{ word: <span className='pk-incoming' ref={incomingRef} lang={zhLang} /> }}
              />
            </p>
          )}
          <ul className='pk-list'>
            {rows.map((entry) => {
              const isOpen = !swap && openRow === entry.word
              const toggle = () => setOpenRow(isOpen ? null : entry.word)
              return (
                <li
                  key={entry.word}
                  data-row={entry.word}
                  className={`pk-item${isOpen ? ' open' : ''}${leavingRow === entry.word ? ' leaving' : ''}`}
                >
                  <div
                    className='pk-row'
                    {...(swap
                      ? {}
                      : {
                          role: ROW_ROLE,
                          tabIndex: 0,
                          'aria-expanded': isOpen,
                          onClick: toggle,
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggle()
                            }
                          },
                        })}
                  >
                    <span className='pk-zh' lang={zhLang}>
                      {label(entry.word)}
                    </span>
                    <span className='pk-mid'>
                      <span className='pk-py'>
                        {entry.pinyin}
                        {entry.hasAudio && entry.languageDictionaryId && (
                          <button
                            type='button'
                            className='word-audio-btn'
                            title={t('pocket.playPronunciation')}
                            onClick={(e) => {
                              e.stopPropagation()
                              playWordAudio({ _id: entry.languageDictionaryId as string, has_audio: true })
                            }}
                          >
                            <AudioPlayIcon />
                          </button>
                        )}
                      </span>
                      {(() => {
                        const { primary } = wordMeaning(entry, entry.meaning)
                        return (
                          <span className='pk-en' lang={primary.lang}>
                            {primary.text}
                          </span>
                        )
                      })()}
                    </span>
                    {swap ? (
                      <button type='button' className='pk-take' onClick={() => swapOut(entry.word)}>
                        {t('pocket.takeOut')}
                      </button>
                    ) : (
                      <Chevron />
                    )}
                  </div>
                  {!swap && (
                    <div className='pk-more'>
                      <div>
                        <div className='pk-body'>
                          {entry.sentenceWords.length > 0 && <div className='pk-sent'>{renderSentence(entry)}</div>}
                          {entry.sentenceEn && <p className='pk-tr'>{entry.sentenceEn}</p>}
                          <div className='pk-acts'>
                            <button type='button' onClick={() => release(entry.word, 'mastered')}>
                              {t('pocket.markMastered')}
                            </button>
                            <button type='button' className='quiet' onClick={() => release(entry.word, 'remove')}>
                              {t('pocket.remove')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {show && (
        <WordDetail
          ref={panelRef}
          visible={detailVisible}
          setWordDetailVisible={setDetailVisible}
          setActiveIndex={setActiveIndex}
          setOpenExampleModal={setOpenExampleModal}
          openExampleModal={openExampleModal}
          isMasteryOpen={isMasteryOpen}
          setMasteryOpen={setMasteryOpen}
          isImportanceOpen={isImportanceOpen}
          setImportanceOpen={setImportanceOpen}
          showMasterySection={true}
        />
      )}
      {show && (
        <WordExamplesModal
          ref={examplesRef}
          openExampleModal={openExampleModal}
          setOpenExampleModal={setOpenExampleModal}
          setWordDetailVisible={setDetailVisible}
        />
      )}

      <div className='pk-fx' ref={fxRef} />
      <div className={`pk-toast${toast ? ' show' : ''}`} role='status' aria-live='polite'>
        {toast}
      </div>
    </>
  )
}

export default WordPocket
